export interface PageRoute { screen: string; id?: number; date?: string }
export function pageRouteKey(route: PageRoute): string {
  return `${route.screen}${route.id === undefined ? '' : `/${route.id}`}${route.date === undefined ? '' : `/${route.date}`}`
}
// Memory stays in this app instance, is bounded, and is discarded on an identity change.
export class PageScrollMemory {
  private identity = ''
  private current = ''
  private positions = new Map<string, number>()
  visit(identity: string, key: string, leavingY: number): { y: number; changed: boolean } {
    if (identity !== this.identity) { this.positions.clear(); this.current = ''; this.identity = identity }
    if (!identity) return { y: 0, changed: false }
    if (this.current) {
      this.positions.delete(this.current)
      this.positions.set(this.current, Math.max(0, leavingY))
      if (this.positions.size > 50) this.positions.delete(this.positions.keys().next().value!)
    }
    const changed = key !== this.current
    this.current = key
    return { y: this.positions.get(key) ?? 0, changed }
  }
}

// To send and Chats are two halves of the one Messages page: moving between them is the switch sliding, never a new
// page fading in (Liam, 10 Oct 2026: switching "doesnt work cleanly and smoothly").
const HALVES = new Set(['rebook', 'chats'])
export const sameMessagesPage = (from: string, to: string): boolean => from !== to && HALVES.has(from) && HALVES.has(to)

export function ownerPagePresentation(identity: () => string) {
  const memory = new PageScrollMemory()
  let cancel = (): void => {}
  let activeHash = '', activeIdentity = '', lastY = 0, lastScreen = ''
  const remember = (): void => {
    if (window.location.hash === activeHash && identity() === activeIdentity) lastY = window.scrollY
  }
  window.addEventListener('scroll', remember, { passive: true })
  // Native fragment navigation can move the window before hashchange runs.
  // Capture the leaving position before that default action, including Enter on links.
  document.addEventListener('click', remember, { capture: true })
  return (root: HTMLElement, route: PageRoute): void => {
    cancel()
    const who = identity()
    const { y, changed } = memory.visit(who, pageRouteKey(route), lastY)
    const halves = sameMessagesPage(lastScreen, route.screen)
    lastScreen = route.screen
    activeHash = window.location.hash; activeIdentity = who; lastY = y
    root.classList.remove('route-arriving')
    if (!who) return
    let stopped = false, frame = 0, arrived = false
    let fadeTimer = 0, expiry = 0
    const stop = (): void => {
      stopped = true
      observer.disconnect()
      sizeObserver?.disconnect()
      window.cancelAnimationFrame(frame)
      window.clearTimeout(expiry)
      for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.removeEventListener(event, stop)
    }
    const restore = (): void => {
      frame = 0
      if (stopped) return
      if (identity() !== who || !root.isConnected) { stop(); return }
      if (!root.childElementCount) return
      if (changed && !arrived && !halves) {
        arrived = true
        root.classList.add('route-arriving')
        fadeTimer = window.setTimeout(() => root.classList.remove('route-arriving'), 180)
      }
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
      // Wait for asynchronous content rather than accepting a loading page's clamped scroll.
      if (max + 1 >= y) { window.scrollTo({ top: y, left: 0, behavior: 'instant' }); stop() }
    }
    const observer = new MutationObserver(() => {
      if (!frame) frame = window.requestAnimationFrame(restore)
    })
    observer.observe(root, { childList: true, subtree: true })
    const sizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      if (!frame) frame = window.requestAnimationFrame(restore)
    })
    sizeObserver?.observe(root)
    for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.addEventListener(event, stop, { passive: true })
    // A failed/shorter page cannot keep restoration observers alive indefinitely.
    expiry = window.setTimeout(stop, 10000)
    frame = window.requestAnimationFrame(restore)
    cancel = () => { stop(); window.clearTimeout(fadeTimer); root.classList.remove('route-arriving') }
  }
}
